import { ItemZoneProvider, useObjectContext } from '@context/ItemZoneContext'
import { LinkedItemZoneProvider } from '@context/LinkedItemZoneContext'
import { useAppStore } from '@store'
import { useViewportLayout } from '@hooks/useViewportLayout'
import type { ViewportLayout } from '@constants'
import { runWithHistoryBatchAsync } from '@utils/historyRecording'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isLinkedSon from '@utils/sceneObjects/assertions/isLinkedSon'
import getStandardMaterial from '@utils/conversion/getStandardMaterial'
import {
  localizeFabricDisplayName,
  toGroupTitleCase,
} from '@utils/budget/fabricCharacteristics'
import { ui } from '@utils/i18n/uiCopy'
import { API } from '@utils/queryBuilder'
import * as Accordion from '@radix-ui/react-accordion'
import { useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import TexturesGroupWithTextures from './TexturesGroupWithTextures'
import { BackMaterialInfo, Store, TexturesGroup } from '@types'
import { useFetchMenu } from '@hooks/useFetchMenu'
import clsx from 'clsx'

const PANEL_SCROLL_AMOUNT = 300

function usePanelScrollArrow(deps: unknown[] = []) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const [hasInitialized, setHasInitialized] = useState(false)

  useEffect(() => {
    const checkScroll = () => {
      if (!scrollRef.current) return

      const { scrollHeight, clientHeight, scrollTop } = scrollRef.current
      const hasScroll = scrollHeight > clientHeight
      setShowScrollButton(hasScroll)

      const isAtTop = scrollTop === 0
      const isAtBottomPosition = scrollTop + clientHeight >= scrollHeight - 1

      if (isAtTop) {
        setIsAtBottom(false)
        setHasInitialized(true)
      } else if (isAtBottomPosition) {
        setIsAtBottom(true)
        setHasInitialized(true)
      } else if (!hasInitialized) {
        const distanceFromTop = scrollTop
        const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
        setIsAtBottom(distanceFromBottom < distanceFromTop)
        setHasInitialized(true)
      }
    }

    checkScroll()
    const timeout = setTimeout(checkScroll, 100)

    const container = scrollRef.current
    if (container) {
      container.addEventListener('scroll', checkScroll)
      window.addEventListener('resize', checkScroll)
      return () => {
        clearTimeout(timeout)
        container.removeEventListener('scroll', checkScroll)
        window.removeEventListener('resize', checkScroll)
      }
    }

    return () => clearTimeout(timeout)
  }, [hasInitialized, ...deps])

  const handleScrollClick = () => {
    if (!scrollRef.current) return

    scrollRef.current.scrollBy({
      top: isAtBottom ? -PANEL_SCROLL_AMOUNT : PANEL_SCROLL_AMOUNT,
      behavior: 'smooth',
    })
  }

  return { scrollRef, showScrollButton, isAtBottom, handleScrollClick }
}

function PanelScrollArrow({
  visible,
  isAtBottom,
  onClick,
}: {
  visible: boolean
  isAtBottom: boolean
  onClick: () => void
}) {
  if (!visible) return null

  // fixed + right: evita que overflow-hidden del panel recorte el botón (-ml-7).
  // Espejo del menú lateral (left-[299px] + -mr-7): borde izquierdo del panel de texturas.
  return (
    <div
      className='pointer-events-none fixed bottom-3 z-[60] flex justify-start'
      style={{ right: 'clamp(240px, 26vw, 300px)' }}
    >
      <button
        type='button'
        onClick={onClick}
        className='pointer-events-auto -ml-7 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-300/30 bg-gray-200/40 backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:bg-gray-200/60 active:scale-95'
        aria-label={isAtBottom ? 'Subir' : 'Bajar'}
      >
        <svg
          className={`h-5 w-5 shrink-0 text-gray-600/80 transition-transform duration-300 ${
            isAtBottom ? 'rotate-180' : ''
          }`}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M19 14l-7 7m0 0l-7-7m7 7V3'
          />
        </svg>
      </button>
    </div>
  )
}

const linkedUnidadAcabadosCache = new Map<number, UnidadAcabado[]>()
const linkedTexturesByFamilyCache = new Map<string, TextureOption[]>()
const linkedPanelPersistentSelection: {
  textureId: number | null
  acabadoNorm: string | null
  acabadoName: string | null
  textureUrl: string | null
  textureName: string | null
} = {
  textureId: null,
  acabadoNorm: null,
  acabadoName: null,
  textureUrl: null,
  textureName: null,
}

function extractTextureRecord(texturas: any): any | null {
  if (!texturas) return null
  if (Array.isArray(texturas)) return texturas[0] || null
  return texturas
}

function extractTextureId(source: any): number | null {
  if (!source) return null
  const record = extractTextureRecord(source?.texturas != null ? source.texturas : source)
  return (
    Number(
      record?.idTexturas || record?.identificadorTextura || record?.searchID || record?.id || 0,
    ) || null
  )
}

function normalizeTextureUrl(url?: string | null) {
  return String(url || '')
    .split('?')[0]
    .trim()
}

function textureUrlsMatch(a?: string | null, b?: string | null) {
  const left = normalizeTextureUrl(a)
  const right = normalizeTextureUrl(b)
  if (!left || !right) return false
  if (left === right) return true
  try {
    if (decodeURI(left) === decodeURI(right)) return true
  } catch {
    // noop
  }
  const leftName = left.split('/').pop()?.toLowerCase()
  const rightName = right.split('/').pop()?.toLowerCase()
  return Boolean(leftName && rightName && leftName === rightName)
}

function textureNamesMatch(a?: string | null, b?: string | null) {
  const left = String(a || '')
    .trim()
    .toLowerCase()
  const right = String(b || '')
    .trim()
    .toLowerCase()
  return Boolean(left && right && left === right)
}

/** Correspondencia: ATEMPORAL mobile-28-07-2026 (`47ee2081`). */
export function TexturesPanel({
  presentation = 'docked',
}: {
  presentation?: 'docked' | 'embedded'
}) {
  const currentObjectID = useAppStore.use.currentObjectID()
  const item = useAppStore((s) => (currentObjectID ? s.items.get(currentObjectID) : null))
  const closeModal = useAppStore.use.closeModal()
  const isModalOpen = useAppStore((s) => s.isModalOpen)
  const location = useLocation()
  const { menuData } = useFetchMenu('PRINCIPAL', 'PRINCIPAL', null)
  const lastMenuUnitId =
    [...(menuData?.submenus || [])]
      .reverse()
      .find((submenu) => Number(submenu?.idUnidades || 0) > 0)?.idUnidades ?? null

  // Cerrar modal si está abierto cuando se monta este componente
  useEffect(() => {
    closeModal()
  }, [closeModal])

  // Mantener el panel linked estable también cuando no hay selección temporal o es linkedSon.
  const isLinked = !item || isLinkedItem(item) || isLinkedSon(item as any)

  // Verificar si estamos en el gestor de proyectos o si hay un modal abierto
  const isInProjectManager = location.pathname === '/planner/proyectos'
  const shouldBeBelowModal = isModalOpen || isInProjectManager

  // Si hay un modal abierto o estamos en el gestor de proyectos, el TexturesPanel debe estar por debajo
  // Normalmente z-50 está encima del canvas, pero cuando hay modales debe estar por debajo del overlay (z-10)
  const zIndex = shouldBeBelowModal ? 'z-[5]' : 'z-50'

  return (
    <div
      className={
        presentation === 'embedded'
          ? 'responsive-sheet-panel relative flex min-h-0 w-full flex-col'
          : `fixed bottom-0 right-0 top-20 ${zIndex} w-[clamp(240px,26vw,320px)]`
      }
    >
      <div
        className={
          presentation === 'embedded'
            ? 'flex w-full flex-col bg-C/Color1'
            : 'flex h-full flex-col overflow-hidden bg-C/Color1'
        }
      >
        {/* En embedded (bottom sheet) el paso ya lo indica el propio sheet: ocultamos la insignia de paso. */}
        {presentation !== 'embedded' ? (
          <div className='sticky top-0 z-10 bg-C/Color1 px-3 pb-3'>
            <div className='mb-2 rounded-2xl border border-[#D7C9A6]/55 bg-white/95 px-2.5 py-1.5 shadow-[0_6px_16px_rgba(0,0,0,0.06)] backdrop-blur-[2px]'>
              <div className='flex items-center gap-2.5'>
                <span className='flex h-10 w-10 items-center justify-center rounded-full border border-[#C8A85A] bg-gradient-to-b from-[#E1C57A] to-[#C8A85A] text-xl font-extrabold text-white shadow-[0_3px_8px_rgba(200,168,90,0.4)]'>
                  2
                </span>
                <div className='text-left leading-tight'>
                  <p className='text-base font-semibold tracking-[0.02em] text-C/Color3'>
                    {ui('chooseFabricAndColor')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Content */}
        <div
          className={
            presentation === 'embedded'
              ? 'flex w-full flex-col'
              : 'flex flex-1 flex-col overflow-hidden'
          }
        >
          {isLinked ? (
            <LinkedItemZoneProvider>
              <LinkedAcabadosPanel
                defaultUnitId={lastMenuUnitId ?? 10437}
                defaultCode='6-95'
                presentation={presentation}
              />
            </LinkedItemZoneProvider>
          ) : (
            // Modo item regular
            <ItemZoneProvider>
              <TexturesPanelContent />
            </ItemZoneProvider>
          )}
        </div>
      </div>
    </div>
  )
}

type UnidadAcabado = {
  idAcabadoUnidadModeloAcabado?: number
  idAcabadoUnidad?: number
  idGruposDeTexturas?: number
  zona?: string
  modelo?: string
  acabado?: string
  descripcion?: string
  texturas?: {
    idTexturas?: number
    idGruposDeTexturas?: number
    identificadorTextura?: number
    nombre?: string
    descripcion?: string
    url?: string
    factorRepeticionX?: number
    factorRepeticionY?: number
    opacidad?: number
    reflejo?: number
    rugosidad?: number
    color?: number | string
    metal?: number
    brillo?: number
    espejo?: boolean
    miniatura?: string | null
    rugosidadFichero?: string | null
    factorDeEscala?: number
  }
}

type TextureOption = {
  idTexturas?: number
  idGruposDeTexturas?: number
  identificadorTextura?: number
  nombre?: string
  descripcion?: string
  url?: string
  urlMiniatura?: string
  factorRepeticionX?: number
  factorRepeticionY?: number
  opacidad?: number
  reflejo?: number
  rugosidad?: number
  color?: number | string
  metal?: number
  brillo?: number
  espejo?: boolean
  miniatura?: string | null
  rugosidadFichero?: string | null
  factorDeEscala?: number
  tarifa?: string | null
}

type HoverTexturePreview = {
  url: string
  name: string
  x: number
  y: number
  zoomX: number
  zoomY: number
}

/** Vista ampliada al hover: un poco más compacta que antes */
const HOVER_PREVIEW_WIDTH = 246
const HOVER_PREVIEW_HEIGHT = 242
const HOVER_PREVIEW_GAP = 16
const HOVER_PREVIEW_MARGIN = 8
function formatAcabadoLabel(raw?: string) {
  const value = String(raw || '').trim()
  if (!value) return ui('unnamed')
  return localizeFabricDisplayName(value) || value
}

/**
 * Densidad de grid de texturas solo para sheets touch (Desktop docked no usa esto).
 * Correspondencia: ATEMPORAL mobile-28-07-2026 (`47ee2081`).
 */
function getEmbeddedTextureGridClass(layout: ViewportLayout): string {
  if (layout === 'mobile') return 'grid grid-cols-3 gap-1.5'
  if (layout === 'tabletV') return 'grid grid-cols-4 gap-2'
  return 'grid grid-cols-5 gap-2' // tabletH
}

function getEmbeddedSwatchButtonClass(layout: ViewportLayout): string {
  // Alturas fijas → miniaturas compactas (aspect-square a ancho completo las inflaba).
  if (layout === 'mobile')
    return 'relative h-14 w-full overflow-hidden rounded bg-[#f3f3f3] active:opacity-90'
  if (layout === 'tabletV')
    return 'relative h-16 w-full overflow-hidden rounded bg-[#f3f3f3] active:opacity-90'
  return 'relative h-[4.5rem] w-full overflow-hidden rounded bg-[#f3f3f3] active:opacity-90' // tabletH
}

function normalizeAcabadoValue(value?: string) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase()
}

function getTextureDisplayName(tex: TextureOption) {
  const raw = String(tex?.descripcion || tex?.nombre || 'Textura').trim()
  const stripped = raw.replace(/^\d{4}\s*_/, '').trim()
  const display = stripped || raw
  return localizeFabricDisplayName(display) || display
}

function getTextureFamilyName(tex: TextureOption) {
  const display = getTextureDisplayName(tex)
  // "Alessandra 13101" -> "Alessandra"
  // "Carlota 01" -> "Carlota"
  // "Great Linen Atemporal 501" -> "Great Linen Atemporal"
  const parts = display.split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return display
  const last = parts[parts.length - 1]
  if (/^\d+$/.test(last)) return parts.slice(0, -1).join(' ')
  return display
}

/** Tokens del nombre ignorando espacios simples/dobles/tabs (p. ej. "A  B" -> ["A","B"]). */
function textureLabelTokens(s: string) {
  return String(s || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
}

/**
 * Sufijo bajo la miniatura: quita la familia aunque el texto tenga espaciado distinto
 * (p. ej. family "Linen Performance Easywash®" vs display "Linen Performance  Easywash® 60").
 * `startsWith`+`slice` falla ahí porque el espacio doble desalinea longitudes y el prefijo.
 */
function getTextureVariantLabel(tex: TextureOption, familyName: string) {
  const display = getTextureDisplayName(tex).trim()
  const fam = String(familyName || '').trim()
  const displayTok = textureLabelTokens(display)
  const familyTok = textureLabelTokens(fam)

  if (familyTok.length > 0 && displayTok.length >= familyTok.length) {
    let prefixOk = true
    for (let i = 0; i < familyTok.length; i++) {
      if (displayTok[i]?.toLowerCase() !== familyTok[i]?.toLowerCase()) {
        prefixOk = false
        break
      }
    }
    if (prefixOk) {
      const rest = displayTok.slice(familyTok.length)
      if (rest.length > 0) return rest.join(' ')
    }
  }

  const last = displayTok[displayTok.length - 1]
  if (displayTok.length > 1 && /^\d+$/.test(last)) return last

  const tail = display.match(/(\d+)\s*$/)
  if (tail) return tail[1]

  return display
}

function getTarifaLabelForFamily(textures: TextureOption[]) {
  const uniqueTarifas = Array.from(
    new Set(
      (textures || [])
        .map((tex) =>
          String(tex?.tarifa || '')
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean),
    ),
  ).sort((a, b) => compareTarifa(a, b))

  if (uniqueTarifas.length === 0) return '-'
  if (uniqueTarifas.length === 1) return uniqueTarifas[0]
  return uniqueTarifas.join('-')
}

function normalizeTarifa(value?: string | null) {
  return String(value || '')
    .trim()
    .toUpperCase()
}

/** A=0, B=1… Si no hay A, el orden natural empieza por la tarifa más baja disponible. */
function compareTarifa(a: string, b: string) {
  const order = (tarifa: string) => {
    const t = normalizeTarifa(tarifa)
    if (t.length === 1 && t >= 'A' && t <= 'Z') return t.charCodeAt(0) - 'A'.charCodeAt(0)
    if (!t) return Number.MAX_SAFE_INTEGER
    return 100 + t.localeCompare('A', 'es')
  }
  return order(a) - order(b)
}

function getFamilyTarifa(textures: TextureOption[]) {
  const tarifas = textures
    .map((tex) => normalizeTarifa(tex?.tarifa))
    .filter(Boolean)
    .sort(compareTarifa)
  return tarifas[0] || ''
}

function groupTexturesByFamily(textures: TextureOption[]) {
  const grouped = textures.reduce(
    (acc, tex) => {
      const familyName = getTextureFamilyName(tex)
      if (!acc[familyName]) acc[familyName] = []
      acc[familyName].push(tex)
      return acc
    },
    {} as Record<string, TextureOption[]>,
  )

  return Object.entries(grouped).sort(([, texturesA], [, texturesB]) => {
    const tarifaDiff = compareTarifa(getFamilyTarifa(texturesA), getFamilyTarifa(texturesB))
    if (tarifaDiff !== 0) return tarifaDiff
    return getTextureFamilyName(texturesA[0]).localeCompare(
      getTextureFamilyName(texturesB[0]),
      'es',
    )
  })
}

function isPreferredFabricZone(texture: any) {
  const zone = String(
    texture?.zoneDisplayName || texture?.zone || texture?.zoneName || '',
  ).toLowerCase()
  return zone.includes('tela') || zone.includes('estructura') || zone.includes('tejido')
}

function pickSceneTextureForLinkedItem(sceneItem: any) {
  const textures = Array.isArray(sceneItem?.textures) ? sceneItem.textures : []
  if (!textures.length) return null

  // Prioridad: zona tela/estructura (aunque aún no tenga searchID al dibujar).
  const preferredWithId = textures.find(
    (t: any) => isPreferredFabricZone(t) && Number(t?.searchID || t?.id || 0) > 0,
  )
  if (preferredWithId) return preferredWithId

  const preferredAny = textures.find((t: any) => isPreferredFabricZone(t))
  if (preferredAny) return preferredAny

  const firstValid = textures.find((t: any) => Number(t?.searchID || t?.id || 0) > 0)
  return firstValid || textures[0] || null
}

function findTextureInFamily(
  familyTextures: TextureOption[],
  candidates: {
    textureId?: number | null
    altTextureId?: number | null
    url?: string | null
    name?: string | null
  },
) {
  if (!familyTextures.length) return null

  const ids = [candidates.textureId, candidates.altTextureId]
    .map((id) => Number(id || 0))
    .filter((id) => id > 0)

  return (
    familyTextures.find((t) => {
      const tId = Number(t?.idTexturas || 0)
      const tIdent = Number(t?.identificadorTextura || 0)
      if (ids.some((id) => id === tId || id === tIdent)) return true
      if (
        textureUrlsMatch(candidates.url, t?.url) ||
        textureUrlsMatch(candidates.url, t?.urlMiniatura)
      ) {
        return true
      }
      if (
        textureNamesMatch(candidates.name, t?.nombre) ||
        textureNamesMatch(candidates.name, t?.descripcion)
      ) {
        return true
      }
      return false
    }) || null
  )
}

function LinkedAcabadosPanel({
  defaultUnitId,
  defaultCode: _defaultCode,
  presentation = 'docked',
}: {
  defaultUnitId?: number
  defaultCode?: string
  presentation?: 'docked' | 'embedded'
}) {
  const layout = useViewportLayout()
  const currentObjectID = useAppStore.use.currentObjectID()
  const itemsMap = useAppStore((s) => s.items)
  const item = useAppStore((s) => (currentObjectID ? s.items.get(currentObjectID) : null))
  const editZone = useAppStore((s) => s.editZone)
  const setFrameloop = useAppStore.use.setFrameloop()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingFamilyTextures, setIsLoadingFamilyTextures] = useState(false)
  const [acabados, setAcabados] = useState<UnidadAcabado[]>([])
  const [familyTextures, setFamilyTextures] = useState<TextureOption[]>([])
  const [selectedAcabadoIdx, setSelectedAcabadoIdx] = useState<number | null>(null)
  const [selectedTextureKey, setSelectedTextureKey] = useState<string | null>(null)
  const [defaultTextureId, setDefaultTextureId] = useState<number | null>(null)
  const [defaultAcabadoNorm, setDefaultAcabadoNorm] = useState<string | null>(null)
  const [hoverPreview, setHoverPreview] = useState<HoverTexturePreview | null>(null)
  const selectedAcabadoNameRef = useRef<string | null>(null)
  const isFetchingAcabadosRef = useRef(false)
  const fetchGenerationRef = useRef(0)
  const catalogDefaultRef = useRef<{
    textureId: number | null
    textureUrl: string | null
    textureName: string | null
    acabadoNorm: string | null
    acabadoName: string | null
  }>({
    textureId: null,
    textureUrl: null,
    textureName: null,
    acabadoNorm: null,
    acabadoName: null,
  })
  const {
    scrollRef: linkedTexturesScrollRef,
    showScrollButton: showLinkedScrollButton,
    isAtBottom: isLinkedScrollAtBottom,
    handleScrollClick: handleLinkedScrollClick,
  } = usePanelScrollArrow([
    selectedAcabadoIdx,
    familyTextures.length,
    acabados.length,
    isLoadingFamilyTextures,
  ])

  const updateHoverPreview = (event: any, textureUrl: string, textureName: string) => {
    // Solo Desktop (panel docked). En sheets touch no hay hover preview.
    if (presentation !== 'docked' || !textureUrl) return

    const rect = event.currentTarget.getBoundingClientRect()
    const relativeX = ((event.clientX - rect.left) / Math.max(1, rect.width)) * 100
    const relativeY = ((event.clientY - rect.top) / Math.max(1, rect.height)) * 100
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const desiredXRight = event.clientX + HOVER_PREVIEW_GAP
    const desiredXLeft = event.clientX - HOVER_PREVIEW_WIDTH - HOVER_PREVIEW_GAP
    const x =
      desiredXRight + HOVER_PREVIEW_WIDTH + HOVER_PREVIEW_MARGIN <= viewportWidth
        ? desiredXRight
        : Math.max(HOVER_PREVIEW_MARGIN, desiredXLeft)

    const desiredY = event.clientY - HOVER_PREVIEW_GAP
    const y = Math.max(
      HOVER_PREVIEW_MARGIN,
      Math.min(desiredY, viewportHeight - HOVER_PREVIEW_HEIGHT - HOVER_PREVIEW_MARGIN),
    )

    setHoverPreview({
      url: encodeURI(textureUrl),
      name: textureName,
      x,
      y,
      zoomX: Math.max(0, Math.min(100, relativeX)),
      zoomY: Math.max(0, Math.min(100, relativeY)),
    })
  }
  const lastResetItemIdRef = useRef<string | null>(null)
  const frameloopTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const stickyLinkedItemRef = useRef<any>(null)

  const activeLinkedItem = (() => {
    if (item && isLinkedItem(item)) return item
    if (item && isLinkedSon(item as any)) {
      const parentId = (item as any)?.firstParentID
      if (parentId) {
        const parent = itemsMap.get(parentId)
        if (parent && isLinkedItem(parent as any)) return parent
      }
    }
    return stickyLinkedItemRef.current
  })()

  useEffect(() => {
    if (item && isLinkedItem(item)) {
      stickyLinkedItemRef.current = item
      return
    }
    if (item && isLinkedSon(item as any)) {
      const parentId = (item as any)?.firstParentID
      if (!parentId) return
      const parent = itemsMap.get(parentId)
      if (parent && isLinkedItem(parent as any)) {
        stickyLinkedItemRef.current = parent
      }
    }
  }, [item, itemsMap])

  useEffect(() => {
    const itemId = String((activeLinkedItem as any)?.id || '')
    // Solo registramos cambio real de item para depuración.
    // No reseteamos estado aquí para evitar "parpadeo"/reinicio al dibujar nuevas unidades.
    if (!itemId || lastResetItemIdRef.current === itemId) return
    lastResetItemIdRef.current = itemId
  }, [(activeLinkedItem as any)?.id])

  useEffect(() => {
    return () => {
      if (frameloopTimeoutRef.current) {
        clearTimeout(frameloopTimeoutRef.current)
        frameloopTimeoutRef.current = undefined
      }
    }
  }, [])

  useEffect(() => {
    const fetchUnidadAcabados = async () => {
      const generation = ++fetchGenerationRef.current
      try {
        const idUnidades = Number((activeLinkedItem as any)?.idUnidades || defaultUnitId || 10437)
        if (!idUnidades) {
          setAcabados([])
          return
        }

        const cachedAcabados = linkedUnidadAcabadosCache.get(idUnidades)
        const hasCache = Boolean(cachedAcabados && cachedAcabados.length > 0)
        if (!hasCache) {
          setIsLoading(true)
        }
        isFetchingAcabadosRef.current = true

        const unidadInfo = await API().api.unidadesGetUnidadGet(idUnidades, String(idUnidades))
        if (generation !== fetchGenerationRef.current) return

        const payload = (unidadInfo as any)?.data ?? unidadInfo
        const unidadAcabados = Array.isArray(payload?.acabados) ? payload.acabados : []
        const defaultTela =
          unidadAcabados.find((a: any) =>
            String(a?.zona || '')
              .toLowerCase()
              .includes('tela'),
          ) || unidadAcabados[0]
        const idAcabadoUnidad = Number(defaultTela?.idAcabadoUnidad || 0)
        const defaultTexRecord = extractTextureRecord(defaultTela?.texturas)
        const defaultTexIdFromUnidad = extractTextureId(defaultTexRecord)
        const defaultTexUrlFromUnidad = normalizeTextureUrl(defaultTexRecord?.url)
        const defaultTexNameFromUnidad = String(
          defaultTexRecord?.nombre || defaultTexRecord?.descripcion || '',
        ).trim()
        const defaultAcabadoFromUnidad = normalizeAcabadoValue(defaultTela?.acabado)

        catalogDefaultRef.current = {
          textureId: defaultTexIdFromUnidad,
          textureUrl: defaultTexUrlFromUnidad || null,
          textureName: defaultTexNameFromUnidad || null,
          acabadoNorm: defaultAcabadoFromUnidad || null,
          acabadoName: defaultTela?.acabado || null,
        }

        // Si el item ya tiene textura aplicada en escena (heredada/cambiada), debe ganar
        // sobre el default de catálogo para que el borde del panel sea consistente.
        const sceneTexture = pickSceneTextureForLinkedItem(activeLinkedItem)
        const sceneTexId =
          Number((sceneTexture as any)?.searchID || 0) ||
          Number((sceneTexture as any)?.id || 0) ||
          null
        const sceneAcabadoNorm = normalizeAcabadoValue((sceneTexture as any)?.acabado)
        const finalDefaultTexId =
          sceneTexId ?? linkedPanelPersistentSelection.textureId ?? defaultTexIdFromUnidad
        const finalDefaultAcabadoNorm =
          sceneAcabadoNorm ||
          linkedPanelPersistentSelection.acabadoNorm ||
          defaultAcabadoFromUnidad ||
          null

        setDefaultTextureId(finalDefaultTexId)
        setDefaultAcabadoNorm(finalDefaultAcabadoNorm)

        // Persistir también el default de catálogo: en la primera unidad dibujada
        // la escena a menudo aún no trae searchID y sin esto el borde no aparece.
        linkedPanelPersistentSelection.textureId =
          finalDefaultTexId ?? linkedPanelPersistentSelection.textureId
        linkedPanelPersistentSelection.acabadoNorm =
          finalDefaultAcabadoNorm ?? linkedPanelPersistentSelection.acabadoNorm
        linkedPanelPersistentSelection.acabadoName =
          (sceneTexture as any)?.acabado ||
          defaultTela?.acabado ||
          linkedPanelPersistentSelection.acabadoName
        linkedPanelPersistentSelection.textureUrl =
          normalizeTextureUrl((sceneTexture as any)?.url) ||
          defaultTexUrlFromUnidad ||
          linkedPanelPersistentSelection.textureUrl
        linkedPanelPersistentSelection.textureName =
          String((sceneTexture as any)?.name || defaultTexNameFromUnidad || '').trim() ||
          linkedPanelPersistentSelection.textureName

        if (hasCache) {
          setAcabados(cachedAcabados!)
          // Forzar rematch del borde con el default recién resuelto.
          setSelectedTextureKey(null)
          return
        }

        if (!idAcabadoUnidad) {
          setAcabados([])
          setSelectedAcabadoIdx(null)
          return
        }

        const zonasInfo = await API().api.acabadosGetAcabadoZonaModeloAcabadoInfo(idAcabadoUnidad)
        if (generation !== fetchGenerationRef.current) return

        const zonasPayload = (zonasInfo as any)?.data ?? zonasInfo
        const zonas = Array.isArray(zonasPayload) ? zonasPayload : []
        const zonaTela =
          zonas.find((z: any) =>
            String(z?.zona || '')
              .toLowerCase()
              .includes('tela'),
          ) || zonas[0]
        const acabadosZona = Array.isArray(zonaTela?.acabados) ? zonaTela.acabados : []

        const list: UnidadAcabado[] = acabadosZona.map((a: any) => {
          return {
            idAcabadoUnidad,
            idAcabadoUnidadModeloAcabado: Number(a?.idAcabadoUnidadModeloAcabado || 0) || undefined,
            idGruposDeTexturas: Number(a?.idGruposDeTexturas || 0) || undefined,
            zona: zonaTela?.zona || 'Tela',
            modelo: zonaTela?.modelo || 'Acabado',
            acabado: a?.acabado,
            descripcion: a?.descripcion,
          } as UnidadAcabado
        })
        linkedUnidadAcabadosCache.set(idUnidades, list)

        setAcabados(list)
        setSelectedTextureKey(null)
        if (list.length === 0) {
          setSelectedAcabadoIdx(null)
          return
        }
      } catch (error) {
        if (generation === fetchGenerationRef.current) {
          setAcabados([])
        }
      } finally {
        if (generation === fetchGenerationRef.current) {
          isFetchingAcabadosRef.current = false
          setIsLoading(false)
        }
      }
    }

    fetchUnidadAcabados()
  }, [defaultUnitId, (activeLinkedItem as any)?.idUnidades, (activeLinkedItem as any)?.id])

  useEffect(() => {
    // Sincronizar SIEMPRE con la textura real aplicada en escena para el item activo.
    // Esto evita que se quede mostrando el default de getUnidad cuando cambia el item
    // pero mantiene el mismo idUnidades.
    const sceneTexture = pickSceneTextureForLinkedItem(activeLinkedItem)
    if (!sceneTexture) return
    const sceneTexId =
      Number((sceneTexture as any)?.searchID || 0) || Number((sceneTexture as any)?.id || 0) || null
    const sceneAcabadoNorm = normalizeAcabadoValue((sceneTexture as any)?.acabado) || null
    const sceneUrl = normalizeTextureUrl((sceneTexture as any)?.url)
    const sceneName = String((sceneTexture as any)?.name || '').trim()
    if (!sceneTexId && !sceneAcabadoNorm && !sceneUrl) return

    // Al dibujar la primera unidad, a menudo hay acabado/URL pero aún no searchID.
    // Conservar default de catálogo / selección persistida para poder pintar el borde.
    const resolvedTexId =
      sceneTexId ??
      linkedPanelPersistentSelection.textureId ??
      catalogDefaultRef.current.textureId ??
      null

    if (resolvedTexId !== null) {
      setDefaultTextureId(resolvedTexId)
      linkedPanelPersistentSelection.textureId = resolvedTexId
    }
    if (sceneAcabadoNorm) {
      setDefaultAcabadoNorm(sceneAcabadoNorm)
      linkedPanelPersistentSelection.acabadoNorm = sceneAcabadoNorm
      linkedPanelPersistentSelection.acabadoName = (sceneTexture as any)?.acabado || null
    }
    if (sceneUrl) {
      linkedPanelPersistentSelection.textureUrl = sceneUrl
    }
    if (sceneName) {
      linkedPanelPersistentSelection.textureName = sceneName
    }

    // Forzar rematch del swatch seleccionado (borde colorAtemporal).
    setSelectedTextureKey(null)
  }, [
    (activeLinkedItem as any)?.id,
    Array.isArray((activeLinkedItem as any)?.textures)
      ? (activeLinkedItem as any).textures
          .map(
            (t: any) =>
              `${t?.zoneName || ''}:${t?.searchID || t?.id || ''}:${t?.url || ''}:${t?.acabado || ''}`,
          )
          .join('|')
      : '',
  ])

  useEffect(() => {
    const hasLinkedSelected = !!activeLinkedItem && isLinkedItem(activeLinkedItem as any)
    if (!hasLinkedSelected) {
      setSelectedAcabadoIdx(null)
      setSelectedTextureKey(null)
      return
    }
    if (!acabados.length) return

    const preservedName =
      selectedAcabadoNameRef.current ||
      linkedPanelPersistentSelection.acabadoName ||
      catalogDefaultRef.current.acabadoName ||
      null
    const selectedIdxByName = preservedName
      ? acabados.findIndex(
          (a) => String(a?.acabado || '').toLowerCase() === String(preservedName).toLowerCase(),
        )
      : -1
    const selectedIdxByDefault = defaultAcabadoNorm
      ? acabados.findIndex((a) => normalizeAcabadoValue(a?.acabado) === defaultAcabadoNorm)
      : -1
    const selectedIdx = selectedIdxByName >= 0 ? selectedIdxByName : selectedIdxByDefault
    const finalIdx = selectedIdx >= 0 ? selectedIdx : 0
    setSelectedAcabadoIdx(finalIdx)
    // Forzar recálculo de textura seleccionada al cargar texturas de la familia.
    setSelectedTextureKey(null)
  }, [(activeLinkedItem as any)?.id, acabados, defaultAcabadoNorm])

  useEffect(() => {
    if (!activeLinkedItem || !isLinkedItem(activeLinkedItem as any)) {
      if (selectedTextureKey !== null) setSelectedTextureKey(null)
      return
    }
    if (selectedAcabadoIdx === null) return
    if (!familyTextures.length) return

    const sceneTexture = pickSceneTextureForLinkedItem(activeLinkedItem)
    const sceneTextureId = Number((sceneTexture as any)?.searchID || 0) || null
    const sceneAltId = Number((sceneTexture as any)?.id || 0) || null
    const sceneTextureUrl = normalizeTextureUrl((sceneTexture as any)?.url)
    const sceneTextureName = String((sceneTexture as any)?.name || '').trim()

    const matchingDefault =
      findTextureInFamily(familyTextures, {
        textureId: defaultTextureId,
        altTextureId: sceneTextureId,
        url: sceneTextureUrl || linkedPanelPersistentSelection.textureUrl,
        name: sceneTextureName || linkedPanelPersistentSelection.textureName,
      }) ||
      findTextureInFamily(familyTextures, {
        textureId: linkedPanelPersistentSelection.textureId,
        altTextureId: sceneAltId,
        url: linkedPanelPersistentSelection.textureUrl,
        name: linkedPanelPersistentSelection.textureName,
      }) ||
      findTextureInFamily(familyTextures, {
        textureId: catalogDefaultRef.current.textureId,
        url: catalogDefaultRef.current.textureUrl,
        name: catalogDefaultRef.current.textureName,
      })

    // Primera unidad / default de catálogo: si el acabado abierto es el de catálogo y
    // aún no hay searchID en escena, marcar al menos la textura default conocida.
    const openAcabadoNorm = normalizeAcabadoValue(acabados[selectedAcabadoIdx]?.acabado)
    const isDefaultAcabadoOpen =
      !!openAcabadoNorm &&
      (openAcabadoNorm === defaultAcabadoNorm ||
        openAcabadoNorm === catalogDefaultRef.current.acabadoNorm ||
        openAcabadoNorm === linkedPanelPersistentSelection.acabadoNorm)

    const resolvedTexture =
      matchingDefault ||
      (isDefaultAcabadoOpen && !sceneTextureId
        ? findTextureInFamily(familyTextures, {
            textureId: catalogDefaultRef.current.textureId || defaultTextureId,
            url: catalogDefaultRef.current.textureUrl,
            name: catalogDefaultRef.current.textureName,
          })
        : null)

    if (!resolvedTexture) return

    const resolvedKey = `${selectedAcabadoIdx}-${
      resolvedTexture.idTexturas || resolvedTexture.identificadorTextura || 'default'
    }`
    if (selectedTextureKey === resolvedKey) return

    setSelectedTextureKey(resolvedKey)
    linkedPanelPersistentSelection.textureId = Number(resolvedTexture.idTexturas || 0) || null
    linkedPanelPersistentSelection.acabadoNorm = openAcabadoNorm || null
    linkedPanelPersistentSelection.acabadoName = acabados[selectedAcabadoIdx]?.acabado || null
    linkedPanelPersistentSelection.textureUrl =
      normalizeTextureUrl(resolvedTexture.url) || linkedPanelPersistentSelection.textureUrl
    linkedPanelPersistentSelection.textureName =
      String(resolvedTexture.nombre || resolvedTexture.descripcion || '').trim() ||
      linkedPanelPersistentSelection.textureName
  }, [
    acabados,
    selectedAcabadoIdx,
    selectedTextureKey,
    activeLinkedItem,
    familyTextures,
    defaultTextureId,
    defaultAcabadoNorm,
  ])

  useEffect(() => {
    const loadFamilyTextures = async () => {
      if (selectedAcabadoIdx === null) {
        setFamilyTextures([])
        return
      }
      const selected = acabados[selectedAcabadoIdx]
      if (!selected) {
        setFamilyTextures([])
        return
      }

      const familyId = Number(selected.idAcabadoUnidadModeloAcabado || 0)
      const groupId = Number(selected.idGruposDeTexturas || 0)
      const cacheKey = familyId > 0 ? `family:${familyId}` : `group:${groupId}`

      if (linkedTexturesByFamilyCache.has(cacheKey)) {
        const cached = linkedTexturesByFamilyCache.get(cacheKey) || []
        if (cached.length > 0) {
          setFamilyTextures(cached)
          return
        }
      }
      // Si el cache existe pero vacío, reintentamos para evitar quedar bloqueados por un fallo transitorio.
      if (linkedTexturesByFamilyCache.has(cacheKey)) {
        linkedTexturesByFamilyCache.delete(cacheKey)
      }

      try {
        setIsLoadingFamilyTextures(true)
        let list: TextureOption[] = []
        if (familyId > 0) {
          const resp = await API().api.acabadosGetTexturas(familyId)
          const payload = (resp as any)?.data ?? resp
          list = Array.isArray(payload) ? payload : []
          // Fallback opcional: si la familia no trae texturas y tenemos grupo, probar por grupo.
          if (list.length === 0 && groupId > 0) {
            const groupResp = await API().api.texturasGetTexturas({
              idGruposDeTexturas: groupId,
              texto: '',
            })
            const groupPayload = (groupResp as any)?.data ?? groupResp
            list = Array.isArray(groupPayload) ? groupPayload : []
          }
        } else if (groupId > 0) {
          const resp = await API().api.texturasGetTexturas({
            idGruposDeTexturas: groupId,
            texto: '',
          })
          const payload = (resp as any)?.data ?? resp
          list = Array.isArray(payload) ? payload : []
        }
        // Fallback final: usar la textura embebida en getUnidad.acabados[] si las APIs de texturas fallan.
        if (
          list.length === 0 &&
          selected?.texturas &&
          String(selected.texturas?.url || '').length > 0
        ) {
          const tex = selected.texturas
          list = [
            {
              idTexturas: Number(tex?.idTexturas || 0) || undefined,
              idGruposDeTexturas:
                Number(tex?.idGruposDeTexturas || selected?.idGruposDeTexturas || 0) || undefined,
              identificadorTextura: Number(tex?.identificadorTextura || 0) || undefined,
              nombre: tex?.nombre || 'Textura',
              descripcion: tex?.descripcion || tex?.nombre || 'Textura',
              url: tex?.url,
              factorRepeticionX: tex?.factorRepeticionX,
              factorRepeticionY: tex?.factorRepeticionY,
              opacidad: tex?.opacidad,
              reflejo: tex?.reflejo,
              rugosidad: tex?.rugosidad,
              color: tex?.color,
              metal: tex?.metal,
              brillo: tex?.brillo,
              espejo: tex?.espejo,
              miniatura: tex?.miniatura,
              rugosidadFichero: tex?.rugosidadFichero,
              factorDeEscala: tex?.factorDeEscala,
            } as TextureOption,
          ]
        }
        linkedTexturesByFamilyCache.set(cacheKey, list)
        setFamilyTextures(list)
      } catch (error) {
        setFamilyTextures([])
      } finally {
        setIsLoadingFamilyTextures(false)
      }
    }

    void loadFamilyTextures()
  }, [selectedAcabadoIdx, acabados])

  // useEffect(() => {
  //   if (!acabados.length) return
  //   console.log(
  //     '[TexturesPanel][mounted-acabados]',
  //     acabados.map((a) => ({
  //       acabado: a?.acabado || '',
  //       modelo: a?.modelo || '',
  //       idAcabadoUnidadModeloAcabado: a?.idAcabadoUnidadModeloAcabado,
  //       idAcabadoUnidad: a?.idAcabadoUnidad,
  //       idGruposDeTexturas: a?.idGruposDeTexturas,
  //     })),
  //   )
  // }, [acabados])

  useEffect(() => {
    if (!selectedTextureKey || !linkedTexturesScrollRef.current) return
    const el = linkedTexturesScrollRef.current.querySelector(
      `[data-texture-key="${selectedTextureKey}"]`,
    ) as HTMLElement | null
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }
  }, [selectedTextureKey, familyTextures])

  if (isLoading) {
    return (
      <div className='flex-1 overflow-hidden p-4 text-center text-C/Color3'>
        {ui('loadingFinishes')}
      </div>
    )
  }

  if (!acabados.length) {
    return (
      <div className='flex-1 overflow-hidden p-4 text-center text-C/Color3'>
        {ui('noFinishes')}
      </div>
    )
  }

  const applyAcabadoTexture = (acabadoEntry: UnidadAcabado, tex: TextureOption) => {
    // const activeBefore = pickSceneTextureForLinkedItem(activeLinkedItem)
    // console.log('[TexturesPanel][before-apply]', {
    //   objectId: (activeLinkedItem as any)?.id,
    //   model: activeBefore?.modelo,
    //   acabado: activeBefore?.acabado,
    //   name: activeBefore?.name,
    //   id: activeBefore?.id,
    //   searchID: activeBefore?.searchID,
    //   groupID: activeBefore?.groupID,
    //   linkedTextureID: activeBefore?.linkedTextureID,
    //   idAcabadoUnidadModeloAcabado: (activeBefore as any)?.idAcabadoUnidadModeloAcabado,
    //   url: activeBefore?.url,
    //   target: {
    //     acabado: acabadoEntry?.acabado,
    //     modelo: acabadoEntry?.modelo,
    //     name: tex?.nombre,
    //     idTexturas: tex?.idTexturas,
    //     idGruposDeTexturas: tex?.idGruposDeTexturas,
    //     idAcabadoUnidadModeloAcabado: acabadoEntry?.idAcabadoUnidadModeloAcabado,
    //     url: tex?.url,
    //   },
    // })

    const boostRenderFrames = () => {
      const previousFrameloop = useAppStore.getState().frameloop
      setFrameloop('always')
      if (frameloopTimeoutRef.current) {
        clearTimeout(frameloopTimeoutRef.current)
        frameloopTimeoutRef.current = undefined
      }
      frameloopTimeoutRef.current = setTimeout(() => {
        setFrameloop(previousFrameloop)
        frameloopTimeoutRef.current = undefined
      }, 900)
      try {
        const r3f = typeof window !== 'undefined' ? (window as any).r3f : undefined
        if (r3f?.invalidate) {
          r3f.invalidate()
          requestAnimationFrame(() => r3f.invalidate?.())
          setTimeout(() => r3f.invalidate?.(), 120)
          setTimeout(() => r3f.invalidate?.(), 260)
        }
      } catch {
        // noop
      }
    }

    if (!tex) {
      return
    }
    if (!tex.url || typeof tex.url !== 'string') {
      return
    }

    const normalizedColor =
      typeof tex.color === 'string' && tex.color.trim().length > 0 ? tex.color : '#FFFFFF'
    const normalizedOpacity =
      tex.opacidad == null || Number.isNaN(Number(tex.opacidad)) ? 1 : Number(tex.opacidad)
    const normalizedUrl = String(tex.url || '').split('?')[0] || String(tex.url || '')

    const backTexture = {
      idTexturas: tex.idTexturas,
      idGruposDeTexturas: tex.idGruposDeTexturas,
      identificadorTextura: tex.identificadorTextura,
      nombre: tex.nombre,
      descripcion: tex.descripcion,
      url: normalizedUrl,
      factorRepeticionX: tex.factorRepeticionX,
      factorRepeticionY: tex.factorRepeticionY,
      opacidad: normalizedOpacity,
      reflejo: tex.reflejo,
      rugosidad: tex.rugosidad,
      color: normalizedColor,
      metal: tex.metal,
      brillo: tex.brillo,
      espejo: tex.espejo,
      miniatura: tex.miniatura,
      rugosidadFichero: tex.rugosidadFichero,
      factorDeEscala: tex.factorDeEscala,
    } as any

    const baseMaterial = getStandardMaterial(
      backTexture,
      acabadoEntry?.acabado || acabadoEntry?.descripcion || '',
      acabadoEntry?.modelo || acabadoEntry?.descripcion || '',
    )
    if (!baseMaterial || typeof baseMaterial !== 'object') {
      return
    }

    // Aplicar globalmente a todas las zonas de items/linkedItems/linkedSons.
    void runWithHistoryBatchAsync(async () => {
      const allSceneItems = Array.from(useAppStore.getState().items.values())
      allSceneItems.forEach((sceneItem: any) => {
        if (!sceneItem) return
        if (!isLinkedItem(sceneItem) && !isItem(sceneItem) && !isLinkedSon(sceneItem)) return
        const zoneNamesFromZones = Object.keys(sceneItem?.zones || {})
        const zoneNamesFromTextures = Array.isArray(sceneItem?.textures)
          ? sceneItem.textures.map((t: any) => t?.zoneName).filter(Boolean)
          : []
        const zoneNames = Array.from(new Set([...zoneNamesFromZones, ...zoneNamesFromTextures]))
        if (!zoneNames.length) return
        zoneNames.forEach((zoneName) => {
          const existingTexture = Array.isArray(sceneItem?.textures)
            ? sceneItem.textures.find((t: any) => t?.zoneName === zoneName)
            : null
          const existingSearchID = Number(existingTexture?.searchID || 0)
          const targetSearchID = Number(tex.idTexturas || 0)
          const existingUrl = String(existingTexture?.url || '').split('?')[0]
          const targetUrl = String(normalizedUrl || '')
          if (
            targetSearchID > 0 &&
            existingSearchID === targetSearchID &&
            existingUrl.length > 0 &&
            existingUrl === targetUrl
          ) {
            return
          }
          const zoneMaterial = {
            ...baseMaterial,
            acabado:
              acabadoEntry?.acabado ?? baseMaterial.acabado ?? existingTexture?.acabado ?? null,
            modelo: acabadoEntry?.modelo ?? baseMaterial.modelo ?? existingTexture?.modelo ?? null,
            groupID: Number(tex.idGruposDeTexturas || existingTexture?.groupID || 0) || undefined,
            searchID: Number(tex.idTexturas || existingTexture?.searchID || 0) || undefined,
            id: Number(tex.idTexturas || existingTexture?.id || 0) || undefined,
            linkedTextureID:
              Number(acabadoEntry?.idAcabadoUnidad || existingTexture?.linkedTextureID || 0) ||
              undefined,
            idAcabadoUnidadModeloAcabado:
              String(
                `${zoneName}_${
                  Number(acabadoEntry?.idAcabadoUnidad || existingTexture?.linkedTextureID || 0) ||
                  ''
                }`,
              ) || undefined,
            name: tex.nombre || existingTexture?.name || '',
            description: tex.descripcion || tex.nombre || existingTexture?.description || '',
            url: normalizedUrl || existingTexture?.url || '',
          }
          editZone(sceneItem.id, zoneName as any, zoneMaterial as any)
        })
      })
    })
    linkedPanelPersistentSelection.textureId = Number(tex.idTexturas || 0) || null
    linkedPanelPersistentSelection.acabadoNorm = normalizeAcabadoValue(acabadoEntry?.acabado)
    linkedPanelPersistentSelection.acabadoName = acabadoEntry?.acabado || null
    linkedPanelPersistentSelection.textureUrl = normalizeTextureUrl(normalizedUrl)
    linkedPanelPersistentSelection.textureName =
      String(tex.nombre || tex.descripcion || '').trim() || null
    boostRenderFrames()
  }

  return (
    <div
      className={
        presentation === 'embedded'
          ? 'relative w-full text-C/Color3'
          : 'relative flex h-full flex-col overflow-hidden text-C/Color3'
      }
    >
      <div
        ref={linkedTexturesScrollRef}
        className={presentation === 'embedded' ? 'p-1.5' : 'flex-1 overflow-y-auto p-1.5'}
      >
        <div className='space-y-1'>
          {acabados.map((acabado, idx) => {
            const isSelected = idx === selectedAcabadoIdx
            return (
              <div
                key={`${acabado.idAcabadoUnidadModeloAcabado || acabado.idAcabadoUnidad || 'a'}-${acabado.acabado || idx}-${idx}`}
                className='rounded bg-transparent'
              >
                <button
                  className={`mb-2 block w-full px-2 py-1.5 text-left text-base font-semibold  ${
                    isSelected ? 'bg-C/Color2 underline' : ''
                  }`}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedAcabadoIdx(null)
                      setSelectedTextureKey(null)
                      return
                    }
                    selectedAcabadoNameRef.current = acabado.acabado || null
                    linkedPanelPersistentSelection.acabadoName = acabado.acabado || null
                    setSelectedTextureKey(null)
                    setSelectedAcabadoIdx(idx)
                  }}
                >
                  {formatAcabadoLabel(acabado.acabado)}
                </button>

                {isSelected && (
                  <div className='px-2 pb-2'>
                    {isLoadingFamilyTextures ? (
                      <div className='py-2 text-xs text-C/Color3'>{ui('loadingTextures')}</div>
                    ) : familyTextures.length > 0 ? (
                      <div className='space-y-3'>
                        {groupTexturesByFamily(familyTextures).map(([familyName, textures]) => {
                          const tarifaLabel = getTarifaLabelForFamily(textures)
                          return (
                            <div
                              key={`${acabado.idAcabadoUnidad || idx}-${familyName}`}
                              className='space-y-2'
                            >
                              <div className='flex items-start justify-between gap-2 border-b border-C/Color2 pb-1'>
                                <span
                                  className='line-clamp-2 min-w-0 flex-1 break-words text-base leading-snug text-C/Color3'
                                  title={familyName}
                                >
                                  {familyName}
                                </span>
                                <span className='shrink-0 whitespace-nowrap text-sm font-semibold leading-snug text-C/Color3'>
                                  {ui('group')} {tarifaLabel}
                                </span>
                              </div>
                              <div
                                className={
                                  presentation === 'embedded'
                                    ? getEmbeddedTextureGridClass(layout)
                                    : 'grid grid-cols-3 gap-2'
                                }
                              >
                                {textures.map((tex) => {
                                  const textureKey = `${idx}-${tex.idTexturas || tex.identificadorTextura || 'default'}`
                                  const isTextureSelected = selectedTextureKey === textureKey
                                  const textureName = getTextureDisplayName(tex)
                                  const textureUrl = tex?.urlMiniatura || tex?.url || ''
                                  return (
                                    <button
                                      key={textureKey}
                                      data-texture-key={textureKey}
                                      className={
                                        presentation === 'embedded'
                                          ? getEmbeddedSwatchButtonClass(layout)
                                          : 'relative w-full overflow-hidden rounded bg-white hover:opacity-90'
                                      }
                                      disabled={
                                        !activeLinkedItem || !isLinkedItem(activeLinkedItem as any)
                                      }
                                      onMouseEnter={(event) =>
                                        updateHoverPreview(event, textureUrl, textureName)
                                      }
                                      onMouseMove={(event) =>
                                        updateHoverPreview(event, textureUrl, textureName)
                                      }
                                      onMouseLeave={() => setHoverPreview(null)}
                                      onClick={() => {
                                        selectedAcabadoNameRef.current = acabado.acabado || null
                                        linkedPanelPersistentSelection.acabadoName =
                                          acabado.acabado || null
                                        setSelectedTextureKey(textureKey)
                                        applyAcabadoTexture(acabado, tex)
                                      }}
                                    >
                                      <img
                                        src={encodeURI(textureUrl)}
                                        alt={textureName}
                                        className={
                                          presentation === 'embedded'
                                            ? 'h-full w-full object-cover'
                                            : 'h-24 w-full object-contain'
                                        }
                                        style={{ imageRendering: 'auto' }}
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement
                                          target.style.display = 'none'
                                        }}
                                      />
                                      {isTextureSelected ? (
                                        <span
                                          aria-hidden='true'
                                          className={clsx(
                                            'pointer-events-none absolute inset-0 z-10 rounded border-colorAtemporal',
                                            presentation === 'embedded'
                                              ? 'border-[3px]'
                                              : 'border-4',
                                          )}
                                        />
                                      ) : null}
                                      <span className='absolute bottom-0.5 left-0.5 z-20 max-w-[calc(100%-0.25rem)] truncate whitespace-nowrap bg-black/70 px-1 text-[10px] font-semibold text-white min-[768px]:bottom-1 min-[768px]:left-1 min-[768px]:text-xs'>
                                        {getTextureVariantLabel(tex, familyName)}
                                      </span>
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <div className='py-2 text-xs text-C/Color3'>
                        No hay texturas para esta familia
                      </div>
                    )}
                    {/*
                     No mostramos mensaje de "Selecciona/dibuja..." porque este panel
                     debe verse limpio como el layout objetivo.
                    */}
                    {/* placeholder to preserve spacing */}
                    {!activeLinkedItem || !isLinkedItem(activeLinkedItem as any) ? (
                      <div className='mt-1 text-[1px] opacity-0'>.</div>
                    ) : null}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
      <PanelScrollArrow
        visible={
          presentation !== 'embedded' && selectedAcabadoIdx !== null && showLinkedScrollButton
        }
        isAtBottom={isLinkedScrollAtBottom}
        onClick={handleLinkedScrollClick}
      />
      {hoverPreview && presentation === 'docked' && (
        <div
          className='pointer-events-none fixed z-[120] rounded-md border border-C/Color2 bg-white p-2 shadow-2xl'
          style={{
            left: hoverPreview.x,
            top: hoverPreview.y,
            width: HOVER_PREVIEW_WIDTH,
          }}
        >
          <div
            className='h-[210px] w-full rounded bg-[#f4f4f4]'
            style={{
              backgroundImage: `url("${hoverPreview.url}")`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '272%',
              backgroundPosition: `${hoverPreview.zoomX}% ${hoverPreview.zoomY}%`,
            }}
          />
          <div className='mt-1 line-clamp-1 text-xs font-medium text-C/Color3'>
            {hoverPreview.name}
          </div>
        </div>
      )}
    </div>
  )
}

/** Sin item seleccionado: el acordeón debe estar controlado para que GroupTextures reciba isExpanded y llame a gettexturas */
function UniversalTextureGroups({ groups }: { groups: TexturesGroup[] }) {
  const { setControlledValues } = useObjectContext()
  const [openGroupId, setOpenGroupId] = useState('')

  useEffect(() => {
    setControlledValues([])
  }, [openGroupId, setControlledValues])

  return (
    <Accordion.Root
      type='single'
      collapsible
      value={openGroupId}
      onValueChange={setOpenGroupId}
    >
      <TexturesGroupWithTextures
        groups={groups}
        nestingLevel={0}
        currentTexture={null}
        onTextureClick={() => {}}
        selectedTextureId={undefined}
        rootAccordionValue={openGroupId}
      />
    </Accordion.Root>
  )
}

function TexturesPanelContent() {
  const {
    object,
    centerInfo,
    controlledValues,
    setControlledValues,
    currentTexture,
    actions: { handleTextureClick: handleTextureClickAction, handleGroupClick, handleZoneClick },
  } = useObjectContext()

  const editZoneFn = useAppStore((s) => s.editZone)

  const currentObjectID = useAppStore((s) => s.currentObjectID)

  const itemFromStore = useAppStore((s) =>
    currentObjectID ? s.items.get(currentObjectID) || s.areas.get(currentObjectID) : null,
  )

  const effectiveObject = object.current || itemFromStore

  const selectedTextureIdFromStore = Array.isArray(effectiveObject?.textures)
    ? effectiveObject.textures.find((t: any) => t.zoneName === currentTexture?.zoneName)?.searchID
    : undefined

  const selectedTextureId = selectedTextureIdFromStore ?? currentTexture?.searchID

  const [localSelectedTextureId, setLocalSelectedTextureId] = useState<number | undefined>(
    selectedTextureId,
  )
  const lastZoneNameRef = useRef<string | undefined>(currentTexture?.zoneName)
  const {
    scrollRef: accordionScrollRef,
    showScrollButton: showAccordionScrollButton,
    isAtBottom: isAccordionScrollAtBottom,
    handleScrollClick: handleAccordionScrollClick,
  } = usePanelScrollArrow([centerInfo?.length, controlledValues[0], localSelectedTextureId])

  useEffect(() => {
    if (currentTexture?.zoneName !== lastZoneNameRef.current) {
      lastZoneNameRef.current = currentTexture?.zoneName
      setLocalSelectedTextureId(selectedTextureId)
    } else if (selectedTextureId !== localSelectedTextureId) {
      setLocalSelectedTextureId(selectedTextureId)
    }
  }, [selectedTextureId, currentTexture?.zoneName, localSelectedTextureId])

  const handleTextureClick = (texture: BackMaterialInfo) => {
    // Actualizar el estado local INMEDIATAMENTE antes de aplicar la textura
    setLocalSelectedTextureId(texture.idTexturas)
    handleTextureClickAction(texture, editZoneFn)
  }

  const handleAccordionChange = async (value: string) => {
    if (!centerInfo) return
    const groupID = value ? Number(value) : 0
    setControlledValues((prev) => {
      const newValues = prev.slice(0, 1)
      newValues[0] = groupID
      return newValues
    })

    if (groupID !== 0) {
      const selectedGroup = findGroupById(centerInfo, groupID)
      if (selectedGroup && (!selectedGroup.subGrupos || selectedGroup.subGrupos.length === 0)) {
        await handleGroupClick(groupID)
      }

      // Scroll automático hasta el grupo expandido
      setTimeout(() => {
        if (accordionScrollRef.current && groupID) {
          const groupElement = accordionScrollRef.current.querySelector(
            `[data-group-id="${groupID}"]`,
          ) as HTMLElement

          if (groupElement) {
            const container = accordionScrollRef.current
            const elementTop = groupElement.offsetTop
            const containerOffsetTop = container.offsetTop || 0
            const desiredScrollTop = elementTop - containerOffsetTop - 20

            container.scrollTo({
              top: Math.max(0, desiredScrollTop),
              behavior: 'smooth',
            })
          }
        }
      }, 100)
    }
  }

  if (
    !effectiveObject ||
    !Array.isArray(effectiveObject.textures) ||
    effectiveObject.textures.length === 0
  ) {
    return (
      <div className='flex-1 overflow-hidden p-4 text-center text-C/Color3'>
        No hay zonas disponibles
      </div>
    )
  }

  // Si no hay zona seleccionada, mostrar mensaje de carga
  if (!currentTexture) {
    return (
      <div className='flex-1 overflow-hidden p-4 text-center text-C/Color3'>Cargando zonas...</div>
    )
  }

  // Si no hay grupos cargados para la zona actual, mostrar mensaje
  if (!centerInfo || centerInfo.length === 0) {
    return (
      <div className='flex-1 overflow-hidden p-4 text-center text-C/Color3'>
        Cargando grupos de texturas...
      </div>
    )
  }

  return (
    <div className='relative flex flex-1 flex-col overflow-hidden'>
      {/* Grupos de texturas */}
      <div
        ref={accordionScrollRef}
        className='textures-panel-scroll flex-1 overflow-y-auto overflow-x-hidden text-C/Color3'
      >
        <Accordion.Root
          type='single'
          collapsible
          value={controlledValues[0] ? String(controlledValues[0]) : ''}
          onValueChange={handleAccordionChange}
        >
          <TexturesGroupWithTextures
            groups={centerInfo}
            nestingLevel={0}
            currentTexture={currentTexture}
            onTextureClick={handleTextureClick}
            selectedTextureId={localSelectedTextureId ?? selectedTextureId}
          />
        </Accordion.Root>
      </div>
      <PanelScrollArrow
        visible={Boolean(controlledValues[0]) && showAccordionScrollButton}
        isAtBottom={isAccordionScrollAtBottom}
        onClick={handleAccordionScrollClick}
      />
    </div>
  )
}

function findGroupById(groups: any[], id: number): any {
  for (const group of groups) {
    if (group.idGruposDeTexturas === id) {
      return group
    }
    if (group.subGrupos) {
      const found = findGroupById(group.subGrupos, id)
      if (found) return found
    }
  }
  return null
}
